import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Grid, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Chip, LinearProgress, List,
  ListItem, ListItemIcon, ListItemText } from '@mui/material';
import { Description, AccountBalance, CheckCircle, HourglassEmpty, CalendarToday } from '@mui/icons-material';
import api from '../../services/api';

const StatCard = ({ title, value, icon, color }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: `${color}15` }}>{icon}</Box>
      <Box>
        <Typography variant="h4" fontWeight="bold">{value}</Typography>
        <Typography variant="body2" color="text.secondary">{title}</Typography>
      </Box>
    </CardContent>
  </Card>
);

const statusLabels = {
  aktiv: { label: 'Aktiv', color: 'success' },
  pausiert: { label: 'Pausiert', color: 'warning' },
  abgeschlossen: { label: 'Abgeschlossen', color: 'info' },
  abgebrochen: { label: 'Abgebrochen', color: 'error' },
};

const DashboardPage = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({ antraege: 0, foerdergeber: 0, bewilligt: 0, offen: 0 });
  const [projekte, setProjekte] = useState([]);
  const [fristen, setFristen] = useState([]);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [antraegeRes, fgRes, projektRes] = await Promise.all([
          api.get('/antraege'),
          api.get('/foerdergeber'),
          api.get('/projekte')
        ]);
        const antraege = antraegeRes.data;
        setStats({
          antraege: antraege.length,
          foerdergeber: fgRes.data.length,
          bewilligt: antraege.filter(a => a.status === 'bewilligt').length,
          offen: antraege.filter(a => ['entwurf', 'in_bearbeitung'].includes(a.status)).length,
        });
        setProjekte(projektRes.data.slice(0, 5));

        // Nächste Fristen: Anträge mit Einreichungsfrist in der Zukunft
        const heute = new Date().toISOString().split('T')[0];
        const upcoming = antraege
          .filter(a => a.einreichungsfrist && a.einreichungsfrist >= heute)
          .sort((a, b) => a.einreichungsfrist.localeCompare(b.einreichungsfrist))
          .slice(0, 5);
        setFristen(upcoming);
      } catch (err) {
        console.error('Fehler beim Laden:', err);
      }
    };
    loadData();
  }, []);

  const calcProgress = (von, bis) => {
    if (!von || !bis) return 0;
    const start = new Date(von).getTime();
    const end = new Date(bis).getTime();
    const now = Date.now();
    if (now <= start) return 0;
    if (now >= end) return 100;
    return Math.round(((now - start) / (end - start)) * 100);
  };

  const daysUntil = (dateStr) => {
    const diff = Math.ceil((new Date(dateStr) - new Date()) / (1000 * 60 * 60 * 24));
    if (diff === 0) return 'Heute';
    if (diff === 1) return 'Morgen';
    return `in ${diff} Tagen`;
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom fontWeight="bold" color="#1B5E20">Dashboard</Typography>

      {/* Statistik-Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Anträge gesamt" value={stats.antraege} icon={<Description sx={{ color: '#1B5E20', fontSize: 32 }} />} color="#1B5E20" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Fördergeber" value={stats.foerdergeber} icon={<AccountBalance sx={{ color: '#1565C0', fontSize: 32 }} />} color="#1565C0" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Bewilligt" value={stats.bewilligt} icon={<CheckCircle sx={{ color: '#2E7D32', fontSize: 32 }} />} color="#2E7D32" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="In Bearbeitung" value={stats.offen} icon={<HourglassEmpty sx={{ color: '#E65100', fontSize: 32 }} />} color="#E65100" />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Aktuelle Projekte */}
        <Grid item xs={12} md={7}>
          <Typography variant="h6" fontWeight="bold" color="#1B5E20" sx={{ mb: 2 }}>Aktuelle Projekte</Typography>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#E8F5E9' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>Titel</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 120 }}>Fortschritt</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {projekte.length === 0 ? (
                  <TableRow><TableCell colSpan={3} align="center" sx={{ py: 3, color: '#999' }}>Noch keine Projekte</TableCell></TableRow>
                ) : projekte.map((p) => {
                  const progress = calcProgress(p.laufzeitVon, p.laufzeitBis);
                  return (
                    <TableRow key={p.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/projekte/${p.id}`)}>
                      <TableCell>{p.titel}</TableCell>
                      <TableCell>
                        <Chip label={statusLabels[p.status]?.label || p.status}
                          color={statusLabels[p.status]?.color || 'default'} size="small" />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LinearProgress variant="determinate" value={progress}
                            sx={{ flexGrow: 1, height: 6, borderRadius: 3,
                              bgcolor: '#E8F5E9', '& .MuiLinearProgress-bar': { bgcolor: '#1B5E20' } }} />
                          <Typography variant="caption" color="text.secondary">{progress}%</Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>

        {/* Nächste Fristen */}
        <Grid item xs={12} md={5}>
          <Typography variant="h6" fontWeight="bold" color="#1B5E20" sx={{ mb: 2 }}>Nächste Fristen</Typography>
          <Paper>
            {fristen.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center', color: '#999' }}>
                <Typography variant="body2">Keine anstehenden Fristen</Typography>
              </Box>
            ) : (
              <List disablePadding>
                {fristen.map((a, idx) => (
                  <ListItem key={a.id} divider={idx < fristen.length - 1}
                    sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#F5F5F5' } }}
                    onClick={() => navigate('/antraege')}>
                    <ListItemIcon>
                      <CalendarToday sx={{ color: '#1B5E20' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={a.titel}
                      secondary={`${new Date(a.einreichungsfrist).toLocaleDateString('de-DE')} (${daysUntil(a.einreichungsfrist)})`}
                    />
                    <Chip label={daysUntil(a.einreichungsfrist)} size="small"
                      sx={{
                        bgcolor: Math.ceil((new Date(a.einreichungsfrist) - new Date()) / 86400000) <= 7 ? '#FFF3E0' : '#E8F5E9',
                        color: Math.ceil((new Date(a.einreichungsfrist) - new Date()) / 86400000) <= 7 ? '#E65100' : '#1B5E20',
                        fontWeight: 'bold'
                      }} />
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DashboardPage;
